/**
 * Iter-115 — External backup provider section.
 *
 * Lives at the top of /projects/:pid/backups. Mirrors every uploaded
 * file (state_entries images + scripts + schedules + change documents)
 * to the user-connected provider asynchronously. OnSetFlow remains
 * the source of truth — backup is one-way.
 *
 * Shows three states:
 *
 *   1. "Connect a backup provider"  — picker listing every registered
 *      provider, with placeholders for Coming-Soon ones.
 *   2. "Connected as alice@example.com"  — connection status + the
 *      Enable / Disable toggle, root-folder rename, Disconnect button,
 *      and a paginated table of recent uploads (Pending / Done /
 *      Failed) with per-row Retry.
 *   3. "Provider not configured" — when the operator hasn't set the
 *      provider's app credentials in backend/.env yet, we surface a
 *      clear "Add DROPBOX_APP_KEY in backend/.env" message instead of
 *      an inert Connect button.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import {
    Cloud,
    CloudOff,
    CheckCircle2,
    AlertCircle,
    RefreshCw,
    Settings2,
    Trash2,
    Pencil,
    Loader2,
    ExternalLink,
    XCircle,
    Clock,
    UploadCloud,
    Save,
    AlertTriangle,
    Copy,
    Check,
    FileText,
    ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "./ui/dialog";


function formatBytes(n) {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTimestamp(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

const STATUS_VISUALS = {
    pending: { label: "Pending", icon: Clock, cls: "text-neutral-400 bg-white/5 border-white/10" },
    uploading: { label: "Uploading", icon: UploadCloud, cls: "text-cyan-300 bg-cyan-500/8 border-cyan-500/25" },
    retrying: { label: "Retrying", icon: RefreshCw, cls: "text-amber-300 bg-amber-500/8 border-amber-500/25" },
    done: { label: "Done", icon: CheckCircle2, cls: "text-emerald-300 bg-emerald-500/8 border-emerald-500/25" },
    failed: { label: "Failed", icon: XCircle, cls: "text-red-300 bg-red-500/8 border-red-500/25" },
    // Iter-118.19 — terminal state for uploads whose source artifact
    // (image / schedule / script / change_doc) was deleted before
    // the upload could complete. Differentiated from "failed" so the
    // user knows there's nothing to retry; styled neutral so it
    // visually de-prioritizes itself in the failed-rows list.
    skipped: { label: "Skipped", icon: AlertCircle, cls: "text-neutral-300 bg-neutral-500/10 border-neutral-500/25" },
};

const SOURCE_LABEL = {
    state_entries: "Image",
    schedules: "Schedule",
    scripts: "Script",
    change_documents: "Document",
};


function ProviderPicker({ providers, onConnect, onShowSetup, connecting }) {
    return (
        <div className="space-y-2" data-testid="backup-provider-picker">
            {providers.map((p) => {
                const disabled = !p.available;
                return (
                    <div
                        key={p.key}
                        data-testid={`backup-provider-${p.key}`}
                        className={`rounded-xl border p-4 flex items-start gap-3 ${
                            disabled
                                ? "border-white/5 bg-card/40 opacity-70"
                                : "border-white/10 bg-card hover:bg-card/80 transition-colors"
                        }`}
                    >
                        <div
                            className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                                disabled
                                    ? "bg-neutral-500/10 text-neutral-500"
                                    : "bg-cyan-500/15 text-cyan-300"
                            }`}
                        >
                            <Cloud size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-white">
                                    {p.label}
                                </span>
                                {!disabled ? (
                                    <span
                                        data-testid={`backup-provider-${p.key}-availability`}
                                        className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/90"
                                    >
                                        {/* Iter-158.13.2.20f.3 #6 — the pill
                                            describes provider AVAILABILITY in
                                            this OnSetFlow deployment, not the
                                            per-Production connection state.
                                            Previous "Active" copy mis-implied
                                            "this Production is connected". */}
                                        Available
                                    </span>
                                ) : (
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                        {p.key === "dropbox" ? "Setup required" : "Coming soon"}
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-neutral-400 mt-1 leading-relaxed">
                                {p.description}
                            </p>
                            {disabled && p.key === "dropbox" ? (
                                /* Iter-118.11 — clearer diagnostic when
                                   the deployed env doesn't have the
                                   Dropbox credentials. Most common cause
                                   of "Connect button missing in deploy"
                                   reports: env vars set in preview's
                                   backend/.env didn't propagate to the
                                   deployed runtime. */
                                <p className="text-[11px] text-amber-300/80 mt-1.5 leading-relaxed">
                                    <strong className="text-amber-200">
                                        Dropbox credentials missing in this environment.
                                    </strong>{" "}
                                    Set <code className="bg-amber-500/15 px-1 py-0.5 rounded text-[10px]">DROPBOX_APP_KEY</code> and <code className="bg-amber-500/15 px-1 py-0.5 rounded text-[10px]">DROPBOX_APP_SECRET</code> in the deployed backend env (preview's <code>backend/.env</code> isn't copied automatically). Tap{" "}
                                    <em>Setup</em> for the full checklist.
                                </p>
                            ) : null}
                            {p.key === "dropbox" && !disabled ? (
                                /* Iter-157P.20 — preflight helper for the
                                   Dropbox user-limit case (Natasha, Feb
                                   2026). While the OAuth app is still
                                   in Development status the 50-user cap
                                   can trigger a Dropbox-side error page
                                   ("This app has reached its user
                                   limit") that terminates on their end
                                   and doesn't redirect back to us. Warn
                                   the producer upfront + reassure them
                                   the project itself does NOT depend on
                                   Dropbox so they can continue safely. */
                                <p className="text-[11px] text-neutral-400 mt-1.5 leading-relaxed">
                                    Dropbox is optional — your project, scripts and photos are stored safely by OnSetFlow either way. If Dropbox shows{" "}
                                    <span className="text-neutral-200">&ldquo;This app has reached its user limit&rdquo;</span>, close that tab and try again shortly; we&rsquo;re raising the limit now.
                                </p>
                            ) : null}
                            {p.key === "dropbox" ? (
                                /* Iter-118.12 — always-on entry point to
                                   the setup walkthrough. Useful even when
                                   keys are configured: users hitting a
                                   redirect_uri mismatch in the deployed
                                   env can pop the dialog and copy the
                                   exact callback URL for THIS environment
                                   without leaving the page. */
                                <button
                                    type="button"
                                    onClick={() => onShowSetup(p.key)}
                                    data-testid="backup-setup-guide-link"
                                    className="instant-tap mt-1.5 inline-flex items-center gap-1 text-[11px] text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
                                >
                                    <Settings2 size={11} />
                                    Need help? Open the setup guide
                                </button>
                            ) : null}
                        </div>
                        {!disabled ? (
                            <Button
                                size="sm"
                                onClick={() => onConnect(p.key)}
                                disabled={connecting}
                                data-testid={`backup-connect-${p.key}`}
                            >
                                {connecting ? (
                                    <Loader2 size={13} className="mr-1.5 animate-spin" />
                                ) : (
                                    <ExternalLink size={13} className="mr-1.5" />
                                )}
                                Connect
                            </Button>
                        ) : p.key === "dropbox" ? (
                            // Iter-116b PART 10 — when keys aren't set,
                            // surface an actionable "Setup instructions"
                            // button so the admin isn't stuck. Opens a
                            // modal with the exact .env keys + Dropbox
                            // app dashboard steps.
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onShowSetup(p.key)}
                                data-testid={`backup-setup-${p.key}`}
                            >
                                <Settings2 size={13} className="mr-1.5" />
                                Setup
                            </Button>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}


function UploadsTable({ uploads, canManage, onRetry, retrying }) {
    if (uploads.length === 0) {
        return (
            <div
                data-testid="backup-uploads-empty"
                className="rounded-xl border border-dashed border-white/10 p-6 text-center"
            >
                <UploadCloud
                    size={20}
                    className="mx-auto text-neutral-500"
                />
                <p className="text-xs text-neutral-400 mt-2">
                    No external backups yet. The next file you upload will sync automatically.
                </p>
            </div>
        );
    }
    return (
        <ul
            data-testid="backup-uploads-list"
            className="rounded-xl border border-white/10 bg-card divide-y divide-white/5"
        >
            {uploads.map((u) => {
                const v = STATUS_VISUALS[u.status] || STATUS_VISUALS.pending;
                const StatusIcon = v.icon;
                const isRetrying = retrying === u.id;
                return (
                    <li
                        key={u.id}
                        data-testid={`backup-upload-${u.id}`}
                        className="px-4 py-3"
                    >
                        <div className="flex items-start gap-3">
                            <div
                                className={`shrink-0 px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1 ${v.cls}`}
                                data-testid={`backup-upload-status-${u.id}`}
                            >
                                <StatusIcon size={10} />
                                {v.label}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                        {SOURCE_LABEL[u.source_collection] || u.source_collection}
                                    </span>
                                    <span className="text-sm text-white truncate font-medium">
                                        {u.file_name}
                                    </span>
                                </div>
                                <div className="text-[11px] text-neutral-500 mt-0.5 truncate font-mono">
                                    {u.remote_path}
                                </div>
                                <div className="text-[10px] text-neutral-500 mt-0.5 flex flex-wrap gap-2">
                                    <span>{formatBytes(u.size)}</span>
                                    <span>·</span>
                                    <span>{formatTimestamp(u.last_attempt_at || u.created_at)}</span>
                                    {u.attempts > 1 ? (
                                        <>
                                            <span>·</span>
                                            <span>{u.attempts} attempts</span>
                                        </>
                                    ) : null}
                                </div>
                                {u.last_error ? (
                                    <div
                                        data-testid={`backup-upload-error-${u.id}`}
                                        className={
                                            // Iter-118.19 — neutral styling for "skipped"
                                            // (source deleted) so it doesn't read like an
                                            // active failure the user has to act on.
                                            // Iter-118.20 — `whitespace-pre-wrap` +
                                            // `break-words` so verbose Dropbox SDK
                                            // messages (often multi-line, sometimes 500-
                                            // 1500 chars) wrap cleanly inside the row
                                            // instead of overflowing or truncating with
                                            // an ellipsis.
                                            (u.status === "skipped"
                                                ? "mt-2 text-[11px] text-neutral-400 px-2 py-1 rounded bg-white/5 border border-white/10"
                                                : "mt-2 text-[11px] text-red-300/90 px-2 py-1 rounded bg-red-500/8 border border-red-500/20") +
                                            " whitespace-pre-wrap break-words leading-snug"
                                        }
                                    >
                                        {u.status === "skipped" ? (
                                            <>
                                                <span className="font-semibold mr-1">
                                                    Cannot retry —
                                                </span>
                                                {u.last_error.replace(/^Cannot retry —\s*/i, "")}
                                            </>
                                        ) : (
                                            <>
                                                {u.last_error_code ? (
                                                    <span
                                                        data-testid={`backup-upload-error-code-${u.id}`}
                                                        className="inline-block mr-1.5 px-1.5 py-0.5 rounded bg-red-500/15 text-red-200 font-mono text-[10px] uppercase tracking-wider"
                                                    >
                                                        {u.last_error_code}
                                                    </span>
                                                ) : null}
                                                {u.last_error}
                                            </>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                            {/* Iter-118.19 — Retry is hidden for "skipped"
                                rows (source no longer exists; retrying
                                is structurally impossible). The neutral
                                error strip above explains why so the
                                user doesn't keep clicking. */}
                            {canManage && u.status === "failed" ? (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onRetry(u)}
                                    disabled={isRetrying}
                                    data-testid={`backup-retry-${u.id}`}
                                >
                                    {isRetrying ? (
                                        <Loader2
                                            size={12}
                                            className="animate-spin"
                                        />
                                    ) : (
                                        <RefreshCw size={12} />
                                    )}
                                </Button>
                            ) : null}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}


export default function ExternalBackupSection({ projectId, canManage }) {
    const navigate = useNavigate();
    // Iter-158.13.2.20e.4 — respect the surrounding ApiBaseProvider so
    // this component works under legacy (`/projects`), H&M
    // (`/beta/hm-app/projects`) and the new Production scope
    // (`/beta/projects`) without duplication. Legacy behaviour is
    // preserved when apiBase falls back to `/projects`.
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const [providers, setProviders] = useState([]);
    const [connection, setConnection] = useState(null);
    const [accountMeta, setAccountMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [connecting, setConnecting] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [error, setError] = useState("");
    const [uploads, setUploads] = useState([]);
    const [retrying, setRetrying] = useState(null);
    const [editingFolder, setEditingFolder] = useState(false);
    const [folderDraft, setFolderDraft] = useState("");
    const [savingFolder, setSavingFolder] = useState(false);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);
    const [togglingEnabled, setTogglingEnabled] = useState(false);
    // Iter-116b PART 10 — actionable setup help dialog.
    const [setupOpen, setSetupOpen] = useState(false);
    // Iter-155B.3 — "Next: Upload Script" pilot guidance card. We
    // fetch the project's script slot status so we can render a calm
    // forward-pointing CTA after backup connection — never let a
    // newly-connected user wonder "what now?".
    const [scriptUploaded, setScriptUploaded] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const [{ data: provData }, { data: connData }] = await Promise.all([
                api.get("/backup/providers"),
                api.get(`${apiBase}/${projectId}/backup/connection`),
            ]);
            setProviders(provData.providers || []);
            setConnection(connData.connection || null);
            setAccountMeta(connData.connection?.account_meta || null);
            if (connData.connection) {
                const { data: ups } = await api.get(
                    `${apiBase}/${projectId}/backup/uploads`,
                    { params: { status: "all", limit: 50 } }
                );
                setUploads(ups || []);
            } else {
                setUploads([]);
            }
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        load();
    }, [load]);

    // Iter-155B.3 — fetch script slot once the connection state is
    // resolved. We only show the "Next: Upload Script" CTA when the
    // user has actually connected backup AND hasn't uploaded a script
    // yet. Probe is silent — any failure simply hides the CTA.
    useEffect(() => {
        if (!projectId || !connection?.enabled) {
            setScriptUploaded(null);
            return;
        }
        let cancelled = false;
        (async () => {
            // Iter-158.13.2.20e.4 — the legacy scalar `/script`
            // endpoint is retired on Route 2 (returns 410). Skip the
            // check under `/beta/*` scopes so the console stays
            // clean; the hint just goes dark, which is the same
            // legacy behaviour when a script hasn't been uploaded.
            if (apiBase !== "/projects") {
                setScriptUploaded(null);
                return;
            }
            try {
                const { data } = await api.get(`/projects/${projectId}/script`);
                if (cancelled) return;
                setScriptUploaded(Boolean(data?.has_original));
            } catch {
                if (!cancelled) setScriptUploaded(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [projectId, connection?.enabled, apiBase]);

    // Iter-118.11 — persistent banner state for OAuth errors. A
    // toast disappears in 4s, but a "Dropbox redirect URI not
    // configured" message needs to stick around so the project
    // owner can act on it. Cleared when the user either fixes the
    // root cause or dismisses the banner.
    const [oauthError, setOauthError] = useState(null);

    // Iter-118.12 — Setup-dialog clipboard helpers. Built from the
    // live `window.location.origin` so the URL the user copies is
    // ALWAYS the right one for the env they're currently in
    // (preview, deployed, custom domain).
    const redirectUri = `${typeof window !== "undefined" ? window.location.origin : ""}/api/backup/oauth/dropbox/callback`;
    const envSnippet = `DROPBOX_APP_KEY="<paste app key>"\nDROPBOX_APP_SECRET="<paste app secret>"`;
    const [copiedKey, setCopiedKey] = useState(null);
    const copyToClipboard = async (text, key) => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                // Fallback for older browsers — never reached on the
                // modern Chrome/Safari we target, but cheap insurance.
                const ta = document.createElement("textarea");
                ta.value = text;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            setCopiedKey(key);
            setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1800);
            toast.success("Copied to clipboard");
        } catch {
            toast.error("Couldn't copy — please copy manually");
        }
    };
    const copyRedirectUri = () => copyToClipboard(redirectUri, "redirect");
    const copyEnvSnippet = () => copyToClipboard(envSnippet, "env");

    // Iter-115 — react to ?backup_status=connected after the OAuth
    // bounce so the user sees a toast + fresh state without manually
    // refreshing.
    // Iter-158.13.2.20f.1 (#17) — clear the previous connection
    // state BEFORE re-loading and surface a small "Verifying…"
    // toast so the "showing previous state until user re-enters
    // Storage" UAT complaint is gone.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const status = params.get("backup_status");
        const detail = params.get("backup_detail");
        if (status === "connected") {
            toast.success("Backup provider connected");
            params.delete("backup_status");
            params.delete("backup_detail");
            const url =
                window.location.pathname +
                (params.toString() ? `?${params.toString()}` : "");
            window.history.replaceState({}, "", url);
            // Iter-158.13.2.20f.1 (#17) — reset stale local state
            // (previous "none"/"paused" chrome) and surface a calm
            // verifying toast BEFORE issuing the refresh request so
            // the UI doesn't linger on the pre-connect snapshot.
            setConnection(null);
            const verifyId = toast.loading("Verifying Dropbox…");
            load().finally(() => toast.dismiss(verifyId));
        } else if (status === "error") {
            // Iter-118.11 — translate Dropbox's error vocabulary into
            // human-friendly diagnostics + persist them on screen.
            const lower = (detail || "").toLowerCase();
            if (
                detail === "dropbox_user_limit_reached" ||
                lower.includes("user limit") ||
                lower.includes("reached its user") ||
                lower.includes("app has reached")
            ) {
                // Iter-157P.20 — Dropbox app is still in Development
                // status and has hit its 50-user cap. This is an
                // account-side quota problem, not an OnSetFlow bug —
                // give the producer a calm, actionable explanation
                // and reassure them the project is safe. Dropbox
                // approval is being applied for; a temporary limit
                // increase is being requested in parallel.
                setOauthError({
                    title: "Dropbox is temporarily at capacity",
                    body:
                        "OnSetFlow's Dropbox connection has reached its current user limit while our production approval is pending. Your project is safe — you can keep working, upload scripts and capture photos as normal. We're raising the limit now and Dropbox linking will re-open shortly.",
                });
            } else if (
                lower.includes("redirect_uri") ||
                lower.includes("redirect uri") ||
                lower.includes("invalid_redirect_uri")
            ) {
                setOauthError({
                    title: "Dropbox redirect URI not configured",
                    body:
                        "The deployed callback URL isn't registered in your Dropbox app. Open the Dropbox app dashboard → OAuth 2 → Redirect URIs and add: " +
                        `${window.location.origin}/api/backup/oauth/dropbox/callback` +
                        ". Then try Connect again.",
                });
            } else if (lower.includes("not configured") || lower.includes("missing")) {
                setOauthError({
                    title: "Dropbox credentials missing in deployed environment",
                    body:
                        "DROPBOX_APP_KEY and/or DROPBOX_APP_SECRET aren't set on the deployed backend. The preview env doesn't propagate automatically — set them in your deployment's environment configuration and redeploy.",
                });
            } else {
                toast.error(detail || "Backup connection failed");
            }
            params.delete("backup_status");
            params.delete("backup_detail");
            const url =
                window.location.pathname +
                (params.toString() ? `?${params.toString()}` : "");
            window.history.replaceState({}, "", url);
        }
    }, [load]);

    // Iter-115 — light polling while ANY upload is in-flight so the
    // user sees status flips without refresh-spamming. Stops as soon
    // as everything's settled.
    useEffect(() => {
        const inFlight = uploads.some(
            (u) => u.status === "pending" || u.status === "uploading" || u.status === "retrying"
        );
        if (!inFlight) return undefined;
        const t = setInterval(() => {
            api
                .get(`${apiBase}/${projectId}/backup/uploads`, {
                    params: { status: "all", limit: 50 },
                })
                .then(({ data }) => setUploads(data || []))
                .catch(() => {});
        }, 4000);
        return () => clearInterval(t);
    }, [uploads, projectId]);

    const onConnect = async (providerKey) => {
        setConnecting(true);
        try {
            // Iter-118.6 — build the OAuth callback URL from the live
            // window origin so it always points at whatever domain
            // the user is on RIGHT NOW (preview, deployed, custom
            // domain). Sending it to the server avoids a per-env
            // BACKUP_OAUTH_BASE_URL config that historically broke
            // the deployed flow when env vars didn't propagate.
            const redirectUri = `${window.location.origin}/api/backup/oauth/${providerKey}/callback`;
            const { data } = await api.post(
                `${apiBase}/${projectId}/backup/connect`,
                { provider: providerKey, redirect_uri: redirectUri }
            );
            // Bounce the user to the provider's authorize URL.
            window.location.href = data.authorize_url;
        } catch (e) {
            toast.error(formatApiError(e));
            setConnecting(false);
        }
    };

    const onToggleEnabled = async () => {
        if (!connection) return;
        setTogglingEnabled(true);
        try {
            const next = !connection.enabled;
            const { data } = await api.patch(
                `${apiBase}/${projectId}/backup/connection`,
                { enabled: next }
            );
            setConnection(data.connection);
            toast.success(next ? "Backup enabled" : "Backup paused");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setTogglingEnabled(false);
        }
    };

    const onSaveFolder = async () => {
        const next = (folderDraft || "").trim();
        if (!next) {
            toast.error("Folder name is required");
            return;
        }
        setSavingFolder(true);
        try {
            const { data } = await api.patch(
                `${apiBase}/${projectId}/backup/connection`,
                { root_folder: next }
            );
            setConnection(data.connection);
            setEditingFolder(false);
            toast.success("Backup folder updated");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSavingFolder(false);
        }
    };

    const onDisconnect = async () => {
        setDisconnecting(true);
        try {
            await api.post(`${apiBase}/${projectId}/backup/disconnect`);
            toast.success("Backup disconnected");
            setConfirmDisconnect(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDisconnecting(false);
        }
    };

    const onRetry = async (u) => {
        setRetrying(u.id);
        try {
            const { data } = await api.post(
                `${apiBase}/${projectId}/backup/uploads/${u.id}/retry`
            );
            setUploads((cur) => cur.map((x) => (x.id === u.id ? data : x)));
            toast.success("Retry queued");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRetrying(null);
        }
    };

    const counts = useMemo(() => {
        const c = { done: 0, failed: 0, in_flight: 0, skipped: 0 };
        for (const u of uploads) {
            if (u.status === "done") c.done += 1;
            else if (u.status === "failed") c.failed += 1;
            // Iter-118.19 — skipped rows are terminal but NOT failures
            // the user can act on; tally them separately so they don't
            // inflate the red "Failed: N" counter.
            else if (u.status === "skipped") c.skipped += 1;
            else c.in_flight += 1;
        }
        return c;
    }, [uploads]);

    if (!canManage) {
        return null;
    }

    return (
        <section
            data-testid="external-backup-section"
            className="mb-8 rounded-2xl border border-white/10 bg-card/40 p-5"
        >
            <header className="flex items-start gap-3 mb-4">
                <div
                    className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                        connection
                            ? "bg-cyan-500/15 text-cyan-300"
                            : "bg-neutral-500/15 text-neutral-400"
                    }`}
                >
                    {connection ? <Cloud size={18} /> : <CloudOff size={18} />}
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg font-bold text-white">
                        External backup
                    </h2>
                    <p className="text-xs text-neutral-400 mt-0.5">
                        Mirror every uploaded file to a third-party storage
                        provider — a one-way safety net. Your project stays
                        accessible in OnSetFlow either way.
                    </p>
                </div>
            </header>

            {error ? (
                <p className="text-xs text-red-300 mb-3">{error}</p>
            ) : null}

            {/* Iter-118.11 — persistent OAuth-error banner. Surfaces
                the two most common deployed-environment failures
                with explicit, actionable copy + a dismiss button.
                Stays on screen until the user fixes the cause or
                taps Dismiss; toasts are too transient for these
                multi-step recovery paths. */}
            {oauthError ? (
                <div
                    data-testid="backup-oauth-error-banner"
                    className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 p-4 flex items-start gap-3"
                >
                    <AlertTriangle
                        size={16}
                        className="text-red-300 mt-0.5 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                        <p
                            data-testid="backup-oauth-error-title"
                            className="text-sm font-semibold text-red-200"
                        >
                            {oauthError.title}
                        </p>
                        <p className="text-xs text-red-200/80 mt-1 leading-relaxed">
                            {oauthError.body}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setOauthError(null)}
                        data-testid="backup-oauth-error-dismiss"
                        className="instant-tap text-[11px] text-red-200/70 hover:text-white px-2 py-1 rounded-md hover:bg-white/5"
                    >
                        Dismiss
                    </button>
                </div>
            ) : null}

            {loading ? (
                <p className="text-xs text-neutral-500 py-6 text-center">
                    Loading…
                </p>
            ) : !connection ? (
                <ProviderPicker
                    providers={providers}
                    onConnect={onConnect}
                    onShowSetup={() => setSetupOpen(true)}
                    connecting={connecting}
                />
            ) : (
                <div className="space-y-4">
                    {/* Iter-155B.3 — "Next: Upload Script" forward-
                        pointing CTA. Renders only when backup is
                        connected & enabled AND no script has been
                        uploaded yet. Closes the previous dead-end
                        where a freshly-connected user was left at
                        /backups with no obvious next move. */}
                    {connection.enabled && scriptUploaded === false ? (
                        <div
                            data-testid="backup-next-step-card"
                            className="rounded-xl border border-emerald-300 bg-emerald-50 dark:border-emerald-500/50 dark:bg-emerald-500/20 p-4"
                        >
                            <div className="flex items-start gap-3">
                                <div className="h-9 w-9 rounded-md bg-emerald-500/25 text-emerald-800 dark:text-emerald-100 flex items-center justify-center shrink-0">
                                    <CheckCircle2 size={16} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p
                                        data-testid="backup-next-step-title"
                                        className="text-sm font-semibold text-emerald-900 dark:text-emerald-50"
                                    >
                                        Backup Connected
                                    </p>
                                    <p
                                        data-testid="backup-next-step-body"
                                        className="text-[13px] text-emerald-900 dark:text-emerald-50 mt-1 leading-relaxed"
                                    >
                                        Every photo, script, schedule and document you add
                                        from now on is mirrored to your{" "}
                                        <span className="capitalize font-medium">
                                            {connection.provider}
                                        </span>
                                        .
                                    </p>
                                    <p
                                        data-testid="backup-next-step-prompt"
                                        className="text-[11px] uppercase tracking-[0.2em] text-emerald-800 dark:text-emerald-200 mt-3 mb-2 font-semibold"
                                    >
                                        Next step
                                    </p>
                                    <Button
                                        type="button"
                                        data-testid="backup-next-step-upload"
                                        onClick={() =>
                                            navigate(
                                                `${routeBase}/${projectId}/script-setup`,
                                            )
                                        }
                                        className="min-tap w-full sm:w-auto"
                                    >
                                        <FileText size={14} className="mr-1.5" />
                                        Upload your first script
                                        <ArrowRight size={14} className="ml-1.5" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* Connection card */}
                    <div
                        data-testid="backup-connection-card"
                        className="rounded-xl border border-white/10 bg-card p-4"
                    >
                        <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-md bg-cyan-500/15 text-cyan-300 flex items-center justify-center shrink-0">
                                <Cloud size={15} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-semibold text-white capitalize">
                                        {connection.provider}
                                    </span>
                                    <span
                                        data-testid="backup-connection-state"
                                        className={`text-[10px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded border ${
                                            connection.enabled
                                                ? "text-emerald-300 bg-emerald-500/8 border-emerald-500/25"
                                                : "text-amber-300 bg-amber-500/8 border-amber-500/25"
                                        }`}
                                    >
                                        {connection.enabled ? "Active" : "Paused"}
                                    </span>
                                </div>
                                <p className="text-xs text-neutral-400 mt-1">
                                    Connected as{" "}
                                    <span className="text-neutral-200">
                                        {connection.account_email || "—"}
                                    </span>
                                    {accountMeta?.allocated_bytes ? (
                                        <>
                                            {" "}· {formatBytes(accountMeta.used_bytes)} of{" "}
                                            {formatBytes(accountMeta.allocated_bytes)} used
                                        </>
                                    ) : null}
                                </p>
                                {accountMeta?.error ? (
                                    <p
                                        data-testid="backup-connection-error"
                                        className="text-[11px] text-red-300/90 mt-1"
                                    >
                                        <AlertCircle size={11} className="inline mr-1" />
                                        {accountMeta.error}
                                    </p>
                                ) : null}

                                {/* Folder editor */}
                                <div className="flex items-center gap-2 mt-3">
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 shrink-0">
                                        Folder
                                    </span>
                                    {editingFolder ? (
                                        <>
                                            <Input
                                                data-testid="backup-folder-input"
                                                value={folderDraft}
                                                onChange={(e) =>
                                                    setFolderDraft(e.target.value)
                                                }
                                                className="h-7 text-xs bg-secondary border-white/10"
                                            />
                                            <Button
                                                size="sm"
                                                onClick={onSaveFolder}
                                                disabled={savingFolder}
                                                data-testid="backup-folder-save"
                                                className="h-7 px-2"
                                            >
                                                {savingFolder ? (
                                                    <Loader2
                                                        size={11}
                                                        className="animate-spin"
                                                    />
                                                ) : (
                                                    <Save size={11} />
                                                )}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setEditingFolder(false)}
                                                className="h-7 px-2"
                                            >
                                                Cancel
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <code
                                                data-testid="backup-folder-display"
                                                className="text-xs px-1.5 py-0.5 rounded bg-secondary border border-white/10 text-neutral-200 font-mono truncate"
                                            >
                                                /{connection.root_folder}
                                            </code>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                    setFolderDraft(connection.root_folder);
                                                    setEditingFolder(true);
                                                }}
                                                data-testid="backup-folder-edit"
                                                className="h-7 px-2 text-neutral-400 hover:text-white"
                                            >
                                                <Pencil size={11} />
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 flex items-center gap-2 flex-wrap">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={onToggleEnabled}
                                disabled={togglingEnabled}
                                data-testid="backup-toggle-enabled"
                            >
                                {togglingEnabled ? (
                                    <Loader2 size={12} className="mr-1.5 animate-spin" />
                                ) : (
                                    <Settings2 size={12} className="mr-1.5" />
                                )}
                                {connection.enabled ? "Pause backup" : "Resume backup"}
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setConfirmDisconnect(true)}
                                data-testid="backup-disconnect-open"
                                className="text-red-300/80 hover:text-red-200 hover:bg-red-500/10"
                            >
                                <Trash2 size={12} className="mr-1.5" />
                                Disconnect
                            </Button>
                            <span className="ml-auto inline-flex items-center gap-3 text-[11px] text-neutral-400">
                                <span className="text-emerald-300">
                                    {counts.done} done
                                </span>
                                {counts.in_flight > 0 ? (
                                    <span className="text-cyan-300">
                                        {counts.in_flight} in flight
                                    </span>
                                ) : null}
                                {counts.failed > 0 ? (
                                    <span className="text-red-300">
                                        {counts.failed} failed
                                    </span>
                                ) : null}
                            </span>
                        </div>
                    </div>

                    {/* Uploads table */}
                    <div>
                        <h3 className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                            Recent uploads
                        </h3>
                        <UploadsTable
                            uploads={uploads}
                            canManage={canManage}
                            onRetry={onRetry}
                            retrying={retrying}
                        />
                    </div>
                </div>
            )}

            <Dialog
                open={confirmDisconnect}
                onOpenChange={setConfirmDisconnect}
            >
                <DialogContent
                    data-testid="backup-disconnect-dialog"
                    className="max-w-sm"
                >
                    <DialogHeader>
                        <DialogTitle>Disconnect backup provider?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-neutral-300 leading-relaxed">
                        Future uploads will stop syncing to{" "}
                        <span className="font-semibold capitalize">
                            {connection?.provider}
                        </span>
                        . Files already mirrored stay where they are. The
                        upload history is preserved so you can reconnect later
                        and continue from the same folder.
                    </p>
                    <DialogFooter>
                        <Button
                            variant="ghost"
                            onClick={() => setConfirmDisconnect(false)}
                            disabled={disconnecting}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={onDisconnect}
                            disabled={disconnecting}
                            data-testid="backup-disconnect-confirm"
                            className="bg-red-500/90 hover:bg-red-500 text-white"
                        >
                            {disconnecting ? (
                                <Loader2 size={13} className="mr-1.5 animate-spin" />
                            ) : (
                                <Trash2 size={13} className="mr-1.5" />
                            )}
                            Disconnect
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Iter-116b PART 10 / Iter-118.12 — Setup help dialog.
                Deploy-aware step-by-step + copy buttons for the
                redirect URI and the env-var snippet so the admin
                can paste them without typos. */}
            <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
                <DialogContent
                    data-testid="backup-setup-dialog"
                    className="max-w-lg max-h-[90vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle>Connect Dropbox — full setup</DialogTitle>
                    </DialogHeader>
                    <div className="text-xs text-neutral-300 space-y-4 leading-relaxed">
                        <ol className="space-y-3 list-decimal pl-5">
                            <li>
                                <strong>Create the Dropbox app.</strong>{" "}
                                Open{" "}
                                <a
                                    href="https://www.dropbox.com/developers/apps"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-cyan-300 underline"
                                >
                                    dropbox.com/developers/apps
                                </a>
                                {" "}→ <strong>Create app</strong>.
                            </li>
                            <li>
                                <strong>Pick the access type:</strong>{" "}
                                <em>Scoped access</em> →{" "}
                                <strong>App folder</strong>{" "}
                                <span className="text-neutral-500">(NOT Full Dropbox — App folder sandboxes uploads to a single folder for safety)</span>
                                . Name it{" "}
                                <code className="bg-secondary px-1 rounded">
                                    OnSetFlow Backup
                                </code>
                                .
                            </li>
                            <li>
                                <strong>Permissions tab.</strong> Tick exactly
                                these scopes, then click <strong>Submit</strong>{" "}
                                at the bottom:
                                <ul className="mt-1.5 space-y-0.5 list-disc pl-5 text-neutral-200">
                                    <li><code>files.content.write</code></li>
                                    <li><code>files.content.read</code></li>
                                    <li><code>files.metadata.read</code></li>
                                    <li><code>account_info.read</code></li>
                                </ul>
                            </li>
                            <li>
                                <strong>Settings tab → OAuth 2 → Redirect URIs.</strong>{" "}
                                Add this exact URL — it must match
                                byte-for-byte (Dropbox is strict):
                                <div
                                    data-testid="backup-setup-redirect-uri-row"
                                    className="mt-2 flex items-center gap-2"
                                >
                                    <code
                                        data-testid="backup-setup-redirect-uri-value"
                                        className="flex-1 bg-secondary px-2 py-1.5 rounded text-[11px] break-all min-w-0"
                                    >
                                        {redirectUri}
                                    </code>
                                    <button
                                        type="button"
                                        data-testid="backup-setup-copy-redirect"
                                        onClick={copyRedirectUri}
                                        title="Copy redirect URI"
                                        className="instant-tap inline-flex items-center gap-1 h-8 px-2 rounded-md text-[11px] bg-secondary hover:bg-white/10 border border-white/10 shrink-0"
                                    >
                                        {copiedKey === "redirect" ? (
                                            <>
                                                <Check size={11} className="text-emerald-300" />
                                                Copied
                                            </>
                                        ) : (
                                            <>
                                                <Copy size={11} />
                                                Copy
                                            </>
                                        )}
                                    </button>
                                </div>
                                <p className="mt-1.5 text-[10px] text-neutral-500">
                                    This URL is built from the page you're
                                    on, so it always matches your current
                                    environment (preview vs deployed). If
                                    you support multiple environments, add
                                    each one's URL separately.
                                </p>
                            </li>
                            <li>
                                <strong>Copy the App key &amp; App secret</strong>{" "}
                                from the Settings tab (top section).
                            </li>
                            <li>
                                <strong>Set the env vars on the deployed backend.</strong>{" "}
                                In Emergent → your project →{" "}
                                <em>Deployment</em> → <em>Environment variables</em>{" "}
                                (deployed runtime), add the two values:
                                <div
                                    data-testid="backup-setup-env-row"
                                    className="mt-2 flex items-stretch gap-2"
                                >
                                    <code
                                        data-testid="backup-setup-env-snippet"
                                        className="flex-1 bg-secondary px-2 py-1.5 rounded text-[11px] whitespace-pre"
                                    >
                                        {`DROPBOX_APP_KEY="<paste app key>"\nDROPBOX_APP_SECRET="<paste app secret>"`}
                                    </code>
                                    <button
                                        type="button"
                                        data-testid="backup-setup-copy-env"
                                        onClick={copyEnvSnippet}
                                        title="Copy env-var snippet"
                                        className="instant-tap inline-flex items-center gap-1 h-8 px-2 rounded-md text-[11px] bg-secondary hover:bg-white/10 border border-white/10 shrink-0 self-start"
                                    >
                                        {copiedKey === "env" ? (
                                            <>
                                                <Check size={11} className="text-emerald-300" />
                                                Copied
                                            </>
                                        ) : (
                                            <>
                                                <Copy size={11} />
                                                Copy
                                            </>
                                        )}
                                    </button>
                                </div>
                                <p className="mt-1.5 text-[10px] text-amber-300/80">
                                    Important: preview's <code>backend/.env</code> values do <strong>not</strong> auto-propagate to deployed builds. They must be set on the deployed runtime separately.
                                </p>
                            </li>
                            <li>
                                <strong>Redeploy</strong> from the Emergent UI
                                (env-var changes need a fresh deploy to take
                                effect).
                            </li>
                            <li>
                                <strong>Test the connection.</strong> Reload
                                this page — the <em>Setup required</em> tag
                                disappears and a green <strong>Connect</strong>{" "}
                                button replaces it. Tap <strong>Connect</strong>,
                                approve in Dropbox, and you'll bounce back
                                here with a <em>Connected</em> badge. From
                                that point every script / change doc /
                                schedule / state-entry queues for upload
                                automatically.
                            </li>
                        </ol>

                        <div className="rounded-md border border-white/10 bg-secondary/40 p-3 text-[11px] text-neutral-400">
                            <p className="font-semibold text-neutral-200 mb-1">
                                Troubleshooting cheat-sheet
                            </p>
                            <ul className="space-y-1 list-disc pl-4">
                                <li>
                                    <strong>Connect button missing</strong> →
                                    env vars not set on the deployed runtime.
                                </li>
                                <li>
                                    <strong>"redirect_uri did not match"</strong> →
                                    the URL you pasted into Dropbox isn't
                                    byte-identical with the one above.
                                </li>
                                <li>
                                    <strong>Connection works in preview but not deployed</strong> →
                                    same redirect URI domain mismatch — add
                                    your prod URL to Dropbox separately.
                                </li>
                            </ul>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            onClick={() => setSetupOpen(false)}
                            data-testid="backup-setup-close"
                        >
                            Got it
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}
